import type { AutoApproveAllowedOpenIssues } from './schema';
import type { AIReviewSettingsValues } from './settings-values';
import type { AIReviewSettings, SensitivityLevel } from './types';

import { DEFAULT_AUTO_APPROVE_BEHAVIOR, DEFAULT_AUTO_APPROVE_MODE } from './auto-approve';
import { DEFAULT_AUTO_ULTRAREVIEW_MODE } from './auto-ultrareview';
import {
  DEFAULT_AUTO_FIX_LOOP_MODE,
  DEFAULT_AUTO_FIX_PROVIDER,
  DEFAULT_VERIFY_MODE,
} from './schema';
import { DEFAULT_ULTRAREVIEW_MODE } from './ultrareview-mode';

import { shouldDisablePrDescriptionGeneration } from '@/constants/installation-overrides';

/** Today's behavior: any open cubic finding blocks auto-approval. */
export const DEFAULT_AUTO_APPROVE_ALLOWED_OPEN_ISSUES: AutoApproveAllowedOpenIssues = 'none';

/**
 * Public-repository PRs from people outside the installation are reviewed but not
 * auto-approved unless a team opts in; matches the column default in schema.prisma.
 */
export const DEFAULT_AUTO_APPROVE_EXCLUDE_EXTERNAL_CONTRIBUTORS = true;

export function getDefaultReviewSensitivityForRepository({
  isPrivate,
}: {
  isPrivate: boolean;
}): SensitivityLevel {
  return isPrivate ? 'medium' : 'high';
}

/**
 * Returns the default values for a new AIReviewSetting record.
 * Visibility-dependent defaults, such as sensitivity, are applied at repo-level
 * creation sites after repository privacy is known.
 */
export function getDefaultAIReviewSettingValues(installationId: number) {
  return {
    generatePrDescriptionEnabled: !shouldDisablePrDescriptionGeneration(installationId),
    prDescriptionReviewLinkEnabled: true,
    skipPrDescriptionIfAuthorProvided: false,
    incrementalReviewsEnabled: true,
    architectureDiagramsEnabled: false,
    externalContributorsRequireManualReview: false,
    autoApproveBehavior: DEFAULT_AUTO_APPROVE_BEHAVIOR,
    autoApproveMode: DEFAULT_AUTO_APPROVE_MODE,
    autoApproveExcludeExternalContributors: DEFAULT_AUTO_APPROVE_EXCLUDE_EXTERNAL_CONTRIBUTORS,
    autoApproveAllowedOpenIssues: DEFAULT_AUTO_APPROVE_ALLOWED_OPEN_ISSUES,
    ultrareviewMode: DEFAULT_ULTRAREVIEW_MODE,
    autoUltrareviewMode: DEFAULT_AUTO_ULTRAREVIEW_MODE,
    mergeConfidenceEnabled: false,
    autoFixButtonEnabled: true,
    autoFixProvider: DEFAULT_AUTO_FIX_PROVIDER,
    autoFixLoopMode: DEFAULT_AUTO_FIX_LOOP_MODE,
    verifyMode: DEFAULT_VERIFY_MODE,
    prCommentFixesEnabled: true,
    fixCommitsToPr: true,
    resolveThreadsWhenAddressed: true,
    maxChangedLines: null,
  };
}

export function getDefaultAIReviewSettingsForRepository({
  installationId,
  repositoryId,
  repositoryIsPrivate,
}: {
  installationId: number;
  repositoryId: number;
  repositoryIsPrivate: boolean;
}): AIReviewSettings {
  return {
    installationId,
    repositoryId,
    generalReviewEnabled: true,
    customInstructions: null,
    documentationLinks: null,
    ignorePatterns: null,
    includePatterns: null,
    autoApproveExcludePatterns: null,
    autoApproveIncludePatterns: null,
    autoApproveFilters: null,
    autoFixLoopFilters: null,
    verifyFilters: null,
    verifyInstructions: null,
    ignoreBranchPatterns: null,
    ignoreBaseBranchPatterns: null,
    ignoreLabels: null,
    ignoreTitlePatterns: null,
    checkDrafts: false,
    summaryInstructions: null,
    codingAgentInstructions: null,
    sensitivity: getDefaultReviewSensitivityForRepository({ isPrivate: repositoryIsPrivate }),
    autoApproveCustomPrompt: null,
    autoUltrareviewCustomPrompt: null,
    autoUltrareviewFilePatterns: null,
    ...getDefaultAIReviewSettingValues(installationId),
  };
}

export function getInheritedInstallationSettingsValues({
  installationId,
  settings,
}: {
  installationId: number;
  repositoryIsPrivate?: boolean;
  settings: AIReviewSettingsValues;
}): AIReviewSettingsValues {
  return {
    ...settings,
    generatePrDescriptionEnabled: shouldDisablePrDescriptionGeneration(installationId)
      ? false
      : settings.generatePrDescriptionEnabled,
    // Installation settings cached in KV are not schema-parsed, so an entry written
    // before this column existed lacks the key until its TTL expires.
    autoApproveAllowedOpenIssues:
      settings.autoApproveAllowedOpenIssues ?? DEFAULT_AUTO_APPROVE_ALLOWED_OPEN_ISSUES,
    // A missing key must not read as the exclusion being turned off.
    autoApproveExcludeExternalContributors:
      settings.autoApproveExcludeExternalContributors ??
      DEFAULT_AUTO_APPROVE_EXCLUDE_EXTERNAL_CONTRIBUTORS,
    autoFixLoopMode: settings.autoFixLoopMode ?? DEFAULT_AUTO_FIX_LOOP_MODE,
    autoFixLoopFilters: settings.autoFixLoopFilters ?? null,
    verifyMode: settings.verifyMode ?? DEFAULT_VERIFY_MODE,
    verifyFilters: settings.verifyFilters ?? null,
    verifyInstructions: settings.verifyInstructions ?? null,
    codingAgentInstructions: settings.codingAgentInstructions ?? null,
  };
}
